import { fabric } from "fabric";
import * as filestack from "filestack-js";
import { EAlignFacesOptions, EFitOptions } from "filestack-js";
import roundNumber from "../../rounded-number";
import {
  BorderedImage,
  BorderedText,
  BorderedTextBox,
} from "../elements/bordered-objects";
import { Textarea } from "../elements/textarea";
import { defaults } from "../state/defaults";
import {
  CircleItem,
  DocumentDesignItem,
  DocumentDesignItemType,
  DocumentDesignState,
  FieldViewModes,
  ImageItem,
  LineItem,
  LinkItem,
  MultipleSelectItem,
  RectItem,
  SignatureItem,
  TextItem,
} from "../state/types";
import { RecordTextJson } from "./../../api/client";
import { Application, BaseRecord } from "./../../api/types";
import { FabricObject } from "./fabric-extended-types";
import { renderFile } from "./fabric-file-renderer";
import { CanvasPaperConfig } from "../../designer/PageDesigner/PageDesigner";
import { FieldElement, renderField } from "./fabric-field-renderer";
import { calculateTopWithPageBreak } from "./fabric-renderer-utils";
import {
  LinkedRecordElement,
  renderLinkedRecord,
} from "./fabric-linked-record-renderer";
import { renderTextareaField } from "./fabric-textarea-renderer";
import { isValidUrl } from "../../helpers/url";
import {
  IEllipseOptions,
  IImageOptions,
  ILineOptions,
  IRectOptions,
  ITextboxOptions,
} from "fabric/fabric-impl";

export interface RenderContext {
  data: Record<string, string>;
  linkedApplications?: Record<string, Application>;
  linkedRecords?: Record<string, RecordTextJson[]>;
  record?: BaseRecord;
  loading: boolean;
  allLinkedRecordsFetched: boolean;
  totalRecords: number;
  application?: Application;
  filestack?: filestack.Client;
  dimensions: CanvasPaperConfig;
}

export function renderCanvas(
  canvas: fabric.Canvas,
  state: DocumentDesignState,
  renderContext: RenderContext,
): FabricObject[] {
  const stateIdsSet = new Set(state.items.map((item) => item.id));
  const elementsMap = new Map<string, fabric.Object>();

  canvas.forEachObject((elem) => {
    if (!elem.selectable && !elem.evented) {
      return;
    }

    if (!stateIdsSet.has((elem as FabricObject).id)) {
      canvas.remove(elem);
    } else {
      elementsMap.set((elem as FabricObject).id, elem);
    }
  });

  const sortedItems = sortItemsByRenderPriority(state.items);

  for (const item of sortedItems) {
    const existingElement = elementsMap.get(item.id);
    const anchorElement =
      item.position === "anchored" && item.anchorId
        ? elementsMap.get(item.anchorId)
        : undefined;

    // Calculate top position of the node element
    let top = item.top ?? 0;
    if (anchorElement) {
      const { height: anchorHeight = 0, top: anchorTop = 0 } = anchorElement;
      top = anchorTop + anchorHeight + (item.anchorDistance ?? 0);
    }

    const updatedElement = renderItem(
      renderContext,
      { ...item, top },
      existingElement,
      canvas,
    );
    if (!existingElement || existingElement !== updatedElement) {
      canvas.add(updatedElement);
      canvas.setActiveObject(updatedElement);
      canvas.bringToFront(updatedElement);

      if (existingElement) {
        existingElement.setOptions({ id: `deleted_${Date.now()}` });
        canvas.remove(existingElement);
      }
    }

    elementsMap.set(item.id, updatedElement);
    updatedElement.setCoords();
  }

  canvas.requestRenderAll();

  return canvas.getObjects() as FabricObject[];
}

export function renderItem(
  context: RenderContext,
  item: DocumentDesignItem,
  element: fabric.Object | undefined,
  canvas: fabric.Canvas,
): fabric.Object {
  // Normalize element top position to avoid page break. Applied only to non-dynamic height elements.
  if (!isDynamicHeightItem(item)) {
    const { updated, newTop } = calculateTopWithPageBreak(
      item,
      context.dimensions,
    );
    if (updated) {
      item.top = newTop;
    }
  }

  switch (item.type) {
    case DocumentDesignItemType.field:
      return renderField(context, item, element as FieldElement);
    case DocumentDesignItemType.rect:
      return renderRect(context, item, element as fabric.Rect);
    case DocumentDesignItemType.circle:
      return renderCircle(context, item, element as fabric.Ellipse);
    case DocumentDesignItemType.line:
      return renderLine(context, item, element as fabric.Line);
    case DocumentDesignItemType.text:
    case DocumentDesignItemType.link:
      return renderText(context, item, element as BorderedTextBox);
    case DocumentDesignItemType.multipleSelect:
      return renderMultipleSelect(context, item, element as BorderedTextBox);
    case DocumentDesignItemType.subitems2:
    case DocumentDesignItemType.linkedRecord:
      return renderLinkedRecord(
        context,
        item,
        element as LinkedRecordElement,
        canvas,
      );
    case DocumentDesignItemType.file:
      return renderFile(context, item, element as fabric.Group, canvas);
    case DocumentDesignItemType.image:
      return renderImage(context, item, element as BorderedImage);
    case DocumentDesignItemType.signature:
      return renderSignature(context, item, element as fabric.Image, canvas);
    case DocumentDesignItemType.textarea:
      return renderTextareaField(context, item, element as Textarea);
    default:
      throw new Error(`No renderer defined for item type`);
  }
}

function renderRect(
  renderContext: RenderContext,
  item: RectItem,
  element: fabric.Rect,
): fabric.Rect {
  const options: IRectOptions = {
    top: item.top,
    left: item.left,
    height: item.height,
    width: item.width,
    angle: item.angle,
    scaleX: item.scaleX,
    scaleY: item.scaleY,
  };

  if (!element) {
    element = new fabric.Rect(options);
  }

  element.setOptions({
    id: item.id,
    ...options,
    stroke: item.stroke ?? defaults.stroke,
    strokeWidth: item.strokeWidth ?? defaults.strokeWidth,
    fill: item.fill ?? defaults.textColor,
    lockScalingFlip: true,
  });

  return element;
}

function renderCircle(
  renderContext: RenderContext,
  item: CircleItem,
  element: fabric.Ellipse,
): fabric.Ellipse {
  const options: IEllipseOptions = {
    top: item.top,
    left: item.left,
    rx: item.width / 2,
    ry: item.height / 2,
    height: item.height,
    width: item.width,
    scaleX: item.scaleX,
    scaleY: item.scaleY,
    angle: item.angle,
  };

  if (!element) {
    element = new fabric.Ellipse(options);
  }

  element.setOptions({
    id: item.id,
    ...options,
    stroke: item.stroke ?? defaults.stroke,
    strokeWidth: item.strokeWidth ?? defaults.strokeWidth,
    fill: item.fill ?? defaults.textColor,
    lockScalingFlip: true,
  });

  return element;
}

function renderLine(
  _renderContext: RenderContext,
  item: LineItem,
  element: fabric.Line,
): fabric.Line {
  const options: ILineOptions = {
    top: item.top,
    left: item.left,
    height: 1,
    width: item.width,
    angle: item.angle,
    scaleX: item.scaleX,
    scaleY: item.scaleY,
  };

  if (!element) {
    element = new fabric.Line([], options);
  }

  element.setOptions({
    id: item.id,
    ...options,
    stroke: item.stroke ?? defaults.stroke,
    strokeWidth: item.strokeWidth ?? defaults.strokeWidth,
    fill: item.fill ?? defaults.textColor,
    lockScalingFlip: true,
  });

  return element;
}

export function renderText(
  renderContext: RenderContext,
  item: TextItem | LinkItem,
  element?: BorderedTextBox,
): BorderedTextBox {
  const options: ITextboxOptions = {
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    angle: item.angle,
    fill: item.fill ?? defaults.textColor,
    fontSize: item.fontSize,
    fontFamily: item.fontFamily,
    fontWeight: item.fontWeight,
    textAlign: item.textAlign,
    lineHeight: item.lineHeight,
  };

  const value = item.template ?? "";

  if (!element) {
    element = new BorderedTextBox(value, options);
  }

  element.setText(value);

  element.setOptions({
    id: item.id,
    ...options,
    lockScalingY: true,
    editable: false,
    data: {
      strokeStyle: item.stroke,
      lineWidth: item.strokeWidth,
      url: "url" in item ? item.url : undefined,
    },
  });

  return element;
}

function renderMultipleSelect(
  renderContext: RenderContext,
  item: MultipleSelectItem,
  element: BorderedTextBox,
): BorderedTextBox {
  const options: ITextboxOptions = {
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    angle: item.angle,
    fill: item.fill ?? defaults.textColor,
    fontSize: item.fontSize,
    fontFamily: item.fontFamily,
    fontWeight: item.fontWeight,
    textAlign: item.textAlign,
    lineHeight: item.lineHeight,
  };

  const value = renderContext.data[item.field_slug] ?? "";

  if (!element) {
    element = new BorderedTextBox(value, options);
  }

  element.setText(value);
  element.setOptions({
    id: item.id,
    ...options,
    lockScalingY: true,
    editable: false,
    data: {
      strokeStyle: item.stroke,
      lineWidth: item.strokeWidth,
    },
  });

  // Field will hold the ID of the options selected. We need to check the available
  // options to obtain their labels. These values are stored inside the 'choices' param
  // of the field's structure.
  const fieldValue =
    (renderContext.record?.[item.field_slug] as string[]) ?? [];
  const fieldStructureObject = renderContext.application?.structure.find(
    (s) => s.slug === item.field_slug,
  );

  const choicesValueLabelMap = fieldStructureObject?.params.choices?.reduce<
    Record<string, string>
  >((acc, c) => {
    acc[c.value] = c.label;
    return acc;
  }, {});
  const selectedChoiceValues = fieldValue.map((v) => choicesValueLabelMap?.[v]);

  switch (item.mode) {
    case FieldViewModes.list:
      element.setText(
        selectedChoiceValues.map((choice) => `•  ${choice}`).join("\n"),
      );
      break;
    case FieldViewModes.single:
      element.setText(selectedChoiceValues.map((choice) => choice).join(", "));
      break;
  }

  const elementHeight = element.height ?? 0;
  const { updated, newTop } = calculateTopWithPageBreak(
    { top: item.top, height: elementHeight },
    renderContext.dimensions,
  );

  if (updated) {
    element.setOptions({ top: newTop });
  }

  return element;
}

function renderImage(
  renderContext: RenderContext,
  item: ImageItem,
  element: BorderedImage,
): fabric.Image {
  const options: IImageOptions = {
    left: item.left,
    top: item.top,
    width: item.width,
    height: item.height,
    angle: item.angle,
  };

  if (!element) {
    element = new BorderedImage(new Image(), options);
  }

  const isURLEmpty = !item.url;
  const isURLValid =
    !!item.url && /^(http|https):\/\//.test(item.url) && isValidUrl(item.url);
  const isLoadingImage = isURLValid && item.handle === undefined;
  const isImageBroken = isURLValid && item.handle === null;

  if (isLoadingImage) {
    element.showLoader();
  } else if (element.isLoading() && (isImageBroken || isURLEmpty)) {
    element.hideLoader();
  }

  if (item.handle) {
    const url = renderContext.filestack?.transform(item.handle, {
      resize: {
        width: item.width,
        height: item.height,
        fit: EFitOptions.max,
        align: EAlignFacesOptions.center,
      },
    });

    if (url && element.getElement().src !== item.url) {
      element.loadImage(url);
    }
  } else {
    element.setElement(new Image(), options);
  }

  element.setOptions({
    id: item.id,
    ...options,
    lockScalingFlip: true,
  });

  if (item.width && item.height && element.width && element.height) {
    element.setOptions({
      scaleX: item.width / element.width,
      scaleY: item.height / element.height,
    });
  } else {
    element.setOptions({
      scaleX: 1,
      scaleY: 1,
    });
  }

  return element;
}

function renderSignature(
  renderContext: RenderContext,
  item: SignatureItem,
  element: fabric.Object,
  canvas: fabric.Canvas,
): fabric.Object {
  if (!element) {
    element = new fabric.Object();
  }

  if (renderContext.record?.[item.field_slug]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const signatureField = renderContext.record[item.field_slug] as any;
    if (
      signatureField?.image_base64 !== undefined &&
      typeof signatureField?.image_base64 === "string" &&
      !!signatureField?.image_base64
    ) {
      const imageSignature = signatureField as { image_base64: string };
      if (!(element instanceof BorderedImage)) {
        canvas.remove(element);
        element = new BorderedImage(new Image());
      }
      fabric.Image.fromURL(imageSignature.image_base64, (img) => {
        if (
          img.getElement().className === "canvas-img" &&
          item.width &&
          img.width &&
          item.height &&
          img.height
        ) {
          const scale = Math.min(
            item.width / img.width,
            item.height / img.height,
          );
          element.setOptions({
            scaleX: roundNumber(scale, 2),
            scaleY: roundNumber(scale, 2),
          });
          (element as BorderedImage).setElement(img.getElement());
        }
        canvas.requestRenderAll();
        element.setCoords();
      });
    } else if (
      signatureField?.text !== undefined &&
      typeof signatureField?.text === "string" &&
      !!signatureField?.text
    ) {
      if (!(element instanceof BorderedText)) {
        canvas.remove(element);
        element = new BorderedText("");
      }

      const textSignature = signatureField as { text: string };
      (element as BorderedText).setOptions({
        text: textSignature.text,
        fontFamily: "AdobeHandwriting-Ernie",
        fontStyle: "normal",
        fill: "#000",
        height: item.height,
        textAlign: "center",
        editable: false,
      });
    } else {
      if (!(element instanceof BorderedTextBox)) {
        canvas.remove(element);
        element = new BorderedTextBox("");
      }
      element.setOptions({
        text: "",
        width: item.width ?? 10,
        height: item.height ?? 10,
        editable: false,
      });
    }
  }

  element.setOptions({
    id: item.id,
    left: item.left,
    top: item.top,
    angle: item.angle,
    lockScalingFlip: true,
  });

  return element;
}

function sortItemsByRenderPriority(
  items: DocumentDesignItem[],
): DocumentDesignItem[] {
  // Split items by absolute and anchor positions. Relative positions are rendered as absolute
  // because the Y position is calculated before rendering.
  let [absoluteItems, anchorItems] = items.reduce<
    [DocumentDesignItem[], DocumentDesignItem[]]
  >(
    (acc, item) => {
      const isAnchorPosition = item.position === "anchored" && item.anchorId;
      if (isAnchorPosition) {
        acc[1].push(item);
      } else {
        acc[0].push(item);
      }

      return acc;
    },
    [[], []],
  );

  // Compose a list of items sorted by render priority. Absolute items first, then relative items.
  // Relative items are added to the list only after their anchors were already added to the list.
  const sortedItems: DocumentDesignItem[] = [...absoluteItems];

  while (anchorItems.length > 0) {
    const renderedAnchorsIds = sortedItems.map((item) => item.id);
    const elementsToRender = anchorItems.filter((item) =>
      renderedAnchorsIds.includes(item.anchorId ?? "invalid-id"),
    );

    sortedItems.push(...elementsToRender);
    anchorItems = anchorItems.filter(
      (item) => !elementsToRender.includes(item),
    );

    // Avoid infinite loop
    if (elementsToRender.length === 0) {
      break;
    }
  }

  return sortedItems;
}

function isDynamicHeightItem(item: DocumentDesignItem): boolean {
  return [
    DocumentDesignItemType.linkedRecord,
    DocumentDesignItemType.subitems2,
    DocumentDesignItemType.field,
    DocumentDesignItemType.textarea,
    DocumentDesignItemType.multipleSelect,
  ].includes(item.type);
}
